import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SummaryApi from '../common';

const EmpTeamLead = () => {
  const [followupData, setFollowupData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [selectedCollege, setSelectedCollege] = useState('');
  const [selectedDistrict, setSelectedDistrict] = useState('');
  const [selectedInterestStatus, setSelectedInterestStatus] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(10);

  const navigate = useNavigate();

  const headers = {
    'Authorization': 'Bearer sandeep_university_916',
    'Content-Type': 'application/json',
  };

  useEffect(() => {
    const fetchFollowupData = async () => {
      try {
        setLoading(true);
        setError(null);

        const encodedEmployeeId = sessionStorage.getItem('H7Z4L9VD2XK5JW8QRYT6');
        if (!encodedEmployeeId) {
          throw new Error('Employee ID not found in session storage. Please log in again.');
        }

        const decodedEmployeeId = atob(encodedEmployeeId);
        const url = SummaryApi.getTlFollowupCountByEmpId.url;

        const response = await fetch(url, {
          method: 'PUT',
          headers,
          body: JSON.stringify({ employee_id: decodedEmployeeId }),
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const result = await response.json();
        if (result.status) {
          setFollowupData(result.data);
          setFilteredData(result.data);
        } else {
          setError('Failed to fetch data: ' + (result.message || 'Unknown error'));
        }
      } catch (e) {
        console.error("Error fetching Team Lead Follow-up data:", e);
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };

    fetchFollowupData();
  }, []);

  // ✅ Filter records (only include students with > 0 follow-ups)
  useEffect(() => {
    let filtered = followupData;

    // ✅ Only show students with more than 0 follow-ups
    filtered = filtered.filter(item => parseInt(item.followup_count) > 0);

    if (selectedCollege) {
      filtered = filtered.filter(item => item.followup_clg_name === selectedCollege);
    }

    if (selectedDistrict) {
      filtered = filtered.filter(item => item.followup_district === selectedDistrict);
    }

    if (selectedInterestStatus !== 'All') {
      filtered = filtered.filter(item => item.followup_is_interested === selectedInterestStatus);
    }

    if (searchTerm) {
      const lowerCaseSearchTerm = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        (item.student_name && item.student_name.toLowerCase().includes(lowerCaseSearchTerm)) ||
        (item.followup_enrollment_code && item.followup_enrollment_code.toLowerCase().includes(lowerCaseSearchTerm)) ||
        (item.student_mobile && item.student_mobile.includes(lowerCaseSearchTerm))
      );
    }

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [selectedCollege, selectedDistrict, selectedInterestStatus, searchTerm, followupData]);

  const uniqueColleges = [...new Set(followupData.map(item => item.followup_clg_name).filter(Boolean))];
  const uniqueDistricts = [...new Set(followupData.map(item => item.followup_district).filter(Boolean))];

  const interestStatuses = ['All', 'Most Interested', 'Interested'];

  const getInterestColorClass = (interestLevel) => {
    switch (interestLevel) {
      case 'Most Interested':
        return 'bg-green-100 text-green-800';
      case 'Interested':
        return 'bg-blue-100 text-blue-800';
      case 'Moderately Interested':
        return 'bg-yellow-100 text-yellow-800';
      case 'Less Interested':
        return 'bg-orange-100 text-orange-800';
      case 'Not Interested':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Pagination logic
  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredData.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredData.length / recordsPerPage);

  const nextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const prevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleRowClick = (studentId) => {
    const encodedStudentId = btoa(studentId.toString());
    navigate(`/emp-team-lead-history/${encodedStudentId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="text-xl font-semibold text-gray-700">Loading Team Lead Follow-up Data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-red-100 text-red-700">
        <div className="text-xl font-semibold">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 bg-gray-50 min-h-screen">
      <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">Team Lead Follow-up</h1>

      <div className='flex flex-col md:flex-row justify-between items-center mb-6'>
        {/* Left Section */}
        <div className="flex flex-col items-center md:items-start w-full md:w-auto mb-4 md:mb-0">
          <div className="flex flex-wrap gap-2 justify-center md:justify-start mb-4">
            {interestStatuses.map((status) => (
              <button
                key={status}
                onClick={() => setSelectedInterestStatus(status)}
                className={`px-4 py-3 rounded-full text-sm font-medium transition-colors duration-200
                  ${selectedInterestStatus === status
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
              >
                {status} ({status === 'All'
                  ? filteredData.length
                  : filteredData.filter(item => item.followup_is_interested === status).length
                })
              </button>
            ))}
          </div>

          <div className='w-full md:w-auto'>
            <input
              type="text"
              placeholder='Search by mobile number, student name, enrollment code'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring focus:ring-indigo-200 w-full md:w-80"
            />
          </div>
        </div>

        {/* Right Section */}
        <div className="flex flex-wrap gap-4 justify-center md:justify-end w-full md:w-auto">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by College</label>
            <select
              value={selectedCollege}
              onChange={(e) => setSelectedCollege(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring focus:ring-indigo-200"
            >
              <option value="">All Colleges</option>
              {uniqueColleges.map((clg, idx) => (
                <option key={idx} value={clg}>{clg}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Filter by District</label>
            <select
              value={selectedDistrict}
              onChange={(e) => setSelectedDistrict(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring focus:ring-indigo-200"
            >
              <option value="">All Districts</option>
              {uniqueDistricts.map((dist, idx) => (
                <option key={idx} value={dist}>{dist}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      {filteredData.length === 0 ? (
        <div className="text-center text-gray-600 text-lg py-8">
          No follow-up data available.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto shadow-lg rounded-lg mb-4">
            <table className="min-w-full bg-white divide-y divide-gray-200">
              <thead className="bg-gray-200">
                <tr>
                  {['S.no', 'Student Name', 'Mobile Number', 'Team Lead Name', 'Enrollment Code', 'District', 'City', 'College Name', 'Interest Level', 'Follow-up Count']
                    .map((header, idx) => (
                      <th key={idx} className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                        {header}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentRecords.map((student, index) => (
                  <tr
                    key={student.student_id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleRowClick(student.student_id)}
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {indexOfFirstRecord + index + 1}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 max-w-[150px] truncate hover:whitespace-normal" title={student.student_name}>
                      {student.student_name}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">{student.mobile_number1 || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{student.team_lead_name}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{student.followup_enrollment_code}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{student.followup_district}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">{student.followup_city}</td>
                    <td className="px-6 py-4 text-sm text-gray-700 max-w-[200px] truncate hover:whitespace-normal" title={student.followup_clg_name}>
                      {student.followup_clg_name}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-2 inline-flex text-xs font-semibold rounded-full ${getInterestColorClass(student.followup_is_interested)}`}>
                        {student.followup_is_interested}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-2 inline-flex text-xs font-semibold rounded-full ${
                        parseInt(student.followup_count) > 0 ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {student.followup_count}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-6">
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-md transition-colors duration-200
                  ${currentPage === 1
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'
                  }`}
              >
                Previous
              </button>
              <span className="text-lg font-semibold text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-md transition-colors duration-200
                  ${currentPage === totalPages
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700 shadow-md'
                  }`}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default EmpTeamLead;
